import { useEffect, useState } from "react";
import { Globe } from "lucide-react";

const LANGUAGES: { code: string; label: string }[] = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "km", label: "ខ្មែរ (Khmer)" },
  { code: "fr", label: "Français" },
];

const setCookie = (name: string, value: string) => {
  document.cookie = `${name}=${value};path=/`;
  const host = window.location.hostname;
  document.cookie = `${name}=${value};path=/;domain=${host}`;
  const parts = host.split(".");
  if (parts.length > 1) {
    document.cookie = `${name}=${value};path=/;domain=.${parts.slice(-2).join(".")}`;
  }
};

const getCurrent = () => {
  const m = document.cookie.match(/googtrans=\/[^/]+\/([^;]+)/);
  return m ? m[1] : "en";
};

const GOOGLE_TRANSLATE_SRC =
  "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";

declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: any;
    __hhGoogleTranslateScriptLoaded?: boolean;
    __hhGoogleTranslateReady?: boolean;
  }
}

const ensureGoogleTranslateContainer = () => {
  if (typeof document === "undefined") return null;

  let container = document.getElementById("google_translate_element");
  if (!container) {
    container = document.createElement("div");
    container.id = "google_translate_element";
    container.style.display = "none";
    document.body.appendChild(container);
  }

  return container;
};

const scheduleIdleTask = (callback: () => void) => {
  if (typeof window === "undefined") return () => undefined;

  if ("requestIdleCallback" in window) {
    const id = window.requestIdleCallback(callback, { timeout: 2500 });
    return () => window.cancelIdleCallback(id);
  }

  const timeout = setTimeout(callback, 1500);
  return () => clearTimeout(timeout);
};

const GoogleTranslate = ({ variant = "light" }: { variant?: "light" | "dark" }) => {
  const [current, setCurrent] = useState("en");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setCurrent(getCurrent());
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const loadTranslateScript = () => {
      ensureGoogleTranslateContainer();

      if (window.__hhGoogleTranslateScriptLoaded || document.querySelector(`script[src="${GOOGLE_TRANSLATE_SRC}"]`)) {
        return;
      }

      window.googleTranslateElementInit = () => {
        ensureGoogleTranslateContainer();
        if (window.__hhGoogleTranslateReady || !window.google?.translate?.TranslateElement) return;

        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            includedLanguages: "en,es,pt,km,fr",
            layout: window.google.translate.TranslateElement.InlineLayout?.SIMPLE,
            autoDisplay: false,
          },
          "google_translate_element"
        );
        window.__hhGoogleTranslateReady = true;
      };

      const script = document.createElement("script");
      script.src = GOOGLE_TRANSLATE_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.__hhGoogleTranslateScriptLoaded = true;
      };
      document.body.appendChild(script);
    };

    const cleanup = scheduleIdleTask(loadTranslateScript);
    return cleanup;
  }, []);

  const handleChange = (code: string) => {
    if (code === "en") {
      setCookie("googtrans", "");
    } else {
      setCookie("googtrans", `/en/${code}`);
    }
    window.location.reload();
  };

  const textColor = variant === "dark" ? "text-white/80 hover:text-white" : "text-foreground/80 hover:text-foreground";

  return (
    <div className="relative notranslate">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`min-h-11 px-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest transition-colors ${textColor}`}
        aria-label="Select language"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Globe size={14} />
        <span>{LANGUAGES.find((l) => l.code === current)?.label ?? "English"}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-44 bg-background border border-border shadow-lg z-50 max-h-80 overflow-auto" role="menu">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => handleChange(l.code)}
              role="menuitem"
              className={`min-h-11 w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                l.code === current ? "text-accent font-semibold" : "text-foreground"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GoogleTranslate;
